<template>
  <div class="tabs" :class="cssClass" aria-label="navigation tab label">
    <!-- In mobile view, the tabs-controls would become a dropdown and this would be the trigger to open it 
         An exact template for the controls will be passed into this as well 
    -->
    <div v-click-outside="handleClickOutside" class="tab-trigger" @click="handleTabTriggerClick">
      <slot name="trigger" />
      <svg-icon name="ArrowDown24" kind="action" />
    </div>

    <ul class="tab-controls">
      <slot name="controls" />
    </ul>

    <div class="tab-content" @scroll="handleScroll">
      <slot name="content" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { clickOutsideDirective } from "@utils/directives";
import { ref, computed } from "vue";

const vClickOutside = clickOutsideDirective["click-outside"];

// Data
const tabTriggerActive = ref(false);

// Props
const props = defineProps({
  container: {
    type: Boolean,
    default: false,
  },
  side: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: "primary",
  },
});

// Computed
const cssClass = computed(() => {
  const { container, side, type } = props;

  return {
    "container-variant": container,
    "tab-expanded": tabTriggerActive.value,
    "side-variant": side,
    [type]: true,
  };
});

// Methods
const handleTabTriggerClick = () => {
  tabTriggerActive.value = !tabTriggerActive.value;
};

const handleClickOutside = () => {
  tabTriggerActive.value = false;
};

const emit = defineEmits<(event: "scroll", e: Event) => void>();

const handleScroll = (e: Event) => {
  emit("scroll", e);
};
</script>

<style lang="scss">
@import "@styles/abstracts/variables";
@import "@styles/abstracts/mixins";
@import "carbon-components/scss/globals/scss/motion.scss";
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.tabs {
  &.side-variant {
    display: grid;
    grid-template-columns: 1fr;

    .tab-content {
      @include media("desktop") {
        height: calc((var(--vh, 1vh) * 100) - $page-banner-min-height - $app-header-height);
      }
      padding: 0 $spacing-05 14rem $spacing-05;
      margin-top: 0;
      background-color: $ui-01;
      overflow: auto;
    }

    .tab-controls-item {
      @include media("desktop") {
        opacity: 1;
      }
      &:hover,
      &.selected {
        opacity: 1;
      }
      .title {
        @include carbon--type-style("panel-title-01");
        @include media("desktop") {
          color: $text-02;
        }
      }
    }
    @include media("desktop") {
      grid-template-columns: min(25%, 292px) auto;
      gap: $spacing-05;
      padding: 0;

      .tab-controls-item {
        position: relative;
        width: unset;
        padding: $spacing-03 $spacing-05;
        border: none;
        border-radius: 10px;
        &:first-child {
          margin-bottom: $spacing-03;
        }
        cursor: pointer;
        &:last-child {
          margin-top: $spacing-05;
          &::before {
            @include position-mixin(
              $height: 1px,
              $width: 100%,
              $left: 0,
              $top: -10px,
              $pseudo: true
            );

            background-color: $ui-04;
          }
        }
        &:hover {
          background-color: $hover-ui;
        }

        &.selected {
          background-color: $ui-06;
        }

        .title {
          padding: $spacing-04 0;
        }
      }
    }

    .tab-controls {
      @include media("desktop") {
        flex-flow: column;
        margin-top: $spacing-05;
        padding-left: 2.5rem;
      }
      @include media("desktop-large") {
        margin-top: $spacing-05;
        padding-left: $spacing-05;
      }
    }

    @include media("tablet-max") {
      &.tab-expanded {
        > .tab-controls {
          max-height: 600px;
          padding: $spacing-05;
          margin: 0 $spacing-05 $spacing-05 $spacing-05;
          visibility: visible;
          &-item {
            cursor: pointer;
          }
        }
      }

      .tab-controls {
        flex-flow: column;
        background-color: $ui-01;
        border-radius: 10px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
        max-height: 0;
        overflow-y: auto;
        visibility: hidden;
        transition: max-height $duration--fast-01 $carbon--ease-in;

        .tab-controls-item {
          width: unset;
          border-bottom: none;
          &:active {
            outline: none;
          }

          &:not(:last-of-type) {
            margin-bottom: $spacing-03;
            border-bottom: $divider-01;
          }
        }
      }
    }
    .tab-trigger {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 2.5rem;
      padding: 0 $spacing-05;
      margin: $spacing-05;
      background-color: $ui-01;
      border-bottom: 1px solid $ui-04;
      border-radius: 10px;
      cursor: pointer;

      @include media("desktop") {
        display: none;
      }

      .tab-controls-item {
        display: none;
        border: none;

        &:active {
          outline: none;
        }

        &.active {
          display: flex;
        }

        .bx--tooltip__trigger {
          // Hide the tooltip for the tab trigger as it would obstruct the options below it
          &::before {
            display: none;
          }

          .bx--assistive-text {
            display: none;
          }
        }
      }

      > .sls-icon {
        min-width: 24px;

        [fill] {
          fill: $interactive-01;
        }
      }
    }
  }
  &:not(.side-variant) {
    .tab-controls-item {
      .title {
        @include carbon--type-style("body-short-01", true);
      }
      &.active {
        .title {
          font-weight: 700;
        }
      }
    }
    @include media("mobile") {
      &.tab-expanded {
        > .tab-controls {
          max-height: 600px;
          padding: $spacing-05;
          visibility: visible;
        }
      }

      .tab-trigger {
        .tab-controls-item {
          .title {
            @include line-clamp(1);
          }
        }
      }

      .tab-controls {
        flex-flow: column;
        background-color: $ui-01;
        border-radius: 10px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
        max-height: 0;
        overflow-y: auto;
        visibility: hidden;
        transition: max-height $duration--fast-01 $carbon--ease-in;

        .tab-controls-item {
          border-bottom: none;

          &:active {
            outline: none;
          }

          &:not(:last-of-type) {
            margin-bottom: $spacing-03;
            border-bottom: $divider-01;
          }
        }
      }
    }

    &.container-variant {
      @include media("tablet-min") {
        .tab-controls-item {
          height: 3rem;
          border-bottom: none;
          border-top: 4px solid $disabled-01;
          border-radius: 10px 10px 0 0;
          padding: $spacing-03 $spacing-05;

          &.active {
            background-color: $ui-01;
            opacity: 1;
            border-color: $interactive-03;
          }
        }

        .tab-content {
          margin: 0;
        }
      }
    }

    .tab-trigger {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 2.5rem;
      padding: 0 $spacing-05;
      background-color: $ui-01;
      border-bottom: 1px solid $ui-04;
      border-radius: 10px;
      cursor: pointer;

      @include media("tablet-min") {
        display: none;
      }

      .tab-controls-item {
        display: none;
        border: none;

        &:active {
          outline: none;
        }

        &.active {
          display: flex;
        }

        .bx--tooltip__trigger {
          // Hide the tooltip for the tab trigger as it would obstruct the options below it
          &::before {
            display: none;
          }

          .bx--assistive-text {
            display: none;
          }
        }
      }

      > .sls-icon {
        min-width: 24px;

        [fill] {
          fill: $interactive-01;
        }
      }
    }

    .tab-controls {
      display: flex;
      width: 100%;

      .tab-controls-item {
        cursor: pointer;
      }
    }

    .tab-content {
      margin-top: $spacing-06;
      > .cv-inline-notification {
        max-width: 100%;
      }
    }
  }

  // secondary dropdown colour for tabs on white background
  &.secondary {
    .tab-trigger {
      background-color: $ui-background;
    }
  }

  .tab-controls-item {
    .title {
      @include media("tablet-min") {
        @include line-clamp(2);
      }
    }
  }
}
</style>
